import { Component, ContentChildren, Input, QueryList, OnDestroy, AfterContentInit, PLATFORM_ID, afterNextRender, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TabsItemDirective } from './directives/tabs-item.directive';

@Component({
  selector: 'black-ui-tabs',
  templateUrl: './ui-tabs.component.html',
  styleUrls: ['./ui-tabs.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class UiTabsComponent implements AfterContentInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);

  @ContentChildren(TabsItemDirective)
  tabsItems!: QueryList<TabsItemDirective>;

  @Input()
  showDefaultTrigger = false;

  selectedIndex = 0;
  private hashChangeListener?: () => void;
  private isBrowser: boolean;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    // Setup hash change listener
    afterNextRender(() => {
      this.hashChangeListener = () => {
        this.syncWithHash();
      };
    });
  }

  ngAfterContentInit() {
    if (!this.isBrowser) return;
    // Wait for tabsItems to be available
    this.tabsItems.changes.subscribe(() => {
      this.syncWithHash();
    });

    // Initial sync with hash
    this.syncWithHash();

    // Listen for hash changes
    if (this.hashChangeListener) {
      window.addEventListener('hashchange', this.hashChangeListener);
    }
  }

  ngOnDestroy() {
    if (!this.isBrowser) return;
    // Clean up listener
    if (this.hashChangeListener) {
      window.removeEventListener('hashchange', this.hashChangeListener);
    }
  }

  private syncWithHash() {
    if (!this.isBrowser) return;
    const hash = window.location.hash.slice(1); // Remove the # symbol
    if (hash && this.tabsItems) {
      const index = this.tabsItems
        .toArray()
        .findIndex((item) => item.name.toLowerCase() === hash.toLowerCase());
      if (index !== -1) {
        this.selectedIndex = index;
      }
    }
  }

  changeSelection(index: number) {
    this.selectedIndex = index;
    if (!this.isBrowser) return;
    const selectedTab = this.tabsItems.toArray()[index];
    if (selectedTab) {
      window.location.hash = selectedTab.name.toLowerCase();
    }
  }

  getChangeSelection(index: number) {
    return this.changeSelection.bind(this, index);
  }

  isSelected(index: number) {
    return this.selectedIndex === index;
  }
}
